// Frontend/src/api/reports.ts
/**
 * Financial reports & dashboard stats API client.
 */

import api from "./client";

// ==================== TYPES ====================

export type PeriodKey =
  | "current_month"
  | "last_month"
  | "3months"
  | "6months"
  | "year";

export interface TrendPoint {
  month: string; // "2026-01"
  collected: number;
  pending: number;
}

export interface FinancialSummaryResponse {
  period_key: PeriodKey;
  period_label: string;
  start_date: string;
  end_date: string;
  total_revenue: number;
  collected: number;
  pending: number;
  overdue: number;
  collection_rate: number; // 0..1
  invoice_count: number;
  trend_data: TrendPoint[];
}

export interface DashboardCounts {
  units: number;
  residents: number;
  open_issues: number;
  pending_payments: number;
}

export interface PaymentsSnapshot {
  pending_proofs: number;
  approved_this_month: number;
  rejected_this_month: number;
  total_collected_this_month: number;
}

export interface DashboardStatsResponse {
  counts: DashboardCounts;
  payments_snapshot: PaymentsSnapshot;
}

// ==================== API FUNCTIONS ====================

/**
 * Get financial summary with trend data for charts.
 */
export async function getFinancialSummary(
  period: PeriodKey = "current_month",
  buildingId?: string
): Promise<FinancialSummaryResponse> {
  const params: any = { period };
  if (buildingId) {
    params.building_id = buildingId;
  }
  const res = await api.get<FinancialSummaryResponse>(
    "/building/payments/invoices/financial-summary/",
    { params }
  );
  return res.data;
}

/**
 * Get dashboard overview stats (counts + payment snapshot).
 */
export async function getDashboardStats(buildingId?: string): Promise<DashboardStatsResponse> {
  const params: any = {};
  if (buildingId) {
    params.building_id = buildingId;
  }
  const res = await api.get<DashboardStatsResponse>(
    "/building/payments/invoices/dashboard-stats/",
    { params }
  );
  return res.data;
}

export interface ReportRequestResponse {
  id: string;
  period: string;
  status: string;
  message: string;
  email: string;
}

/**
 * Request a detailed financial report to be generated and emailed.
 * Rate-limited to 3 requests per 24 hours.
 */
export async function requestReport(
  period: PeriodKey,
  buildingId?: string
): Promise<ReportRequestResponse> {
  const payload: any = { period };
  if (buildingId) {
    payload.building_id = buildingId;
  }
  const res = await api.post<ReportRequestResponse>(
    "/building/payments/invoices/request-report/",
    payload
  );
  return res.data;
}
